import type { CSSProperties, ReactNode } from "react";
import { useState } from "react";

import { useAuth } from "./auth-provider";

export interface StardeckSignInButtonProps {
  size?: "small" | "medium" | "large";
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  disabled?: boolean;
}

const sizeStyles = {
  small: {
    padding: "8px 16px",
    fontSize: "14px",
    gap: "4px",
    borderRadius: "6px",
  },
  medium: {
    padding: "10px 20px",
    fontSize: "16px",
    gap: "6px",
    borderRadius: "8px",
  },
  large: {
    padding: "12px 24px",
    fontSize: "18px",
    gap: "8px",
    borderRadius: "10px",
  },
};

const LoadingSpinner = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      animation: "spin 1s linear infinite",
    }}
  >
    <style>
      {`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}
    </style>
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="3"
      fill="none"
      strokeDasharray="42"
      strokeDashoffset="10"
      strokeLinecap="round"
    />
  </svg>
);

const StardeckLogo = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M35.916 18.7281C33.7968 18.7282 32.3652 18.7381 31.1133 18.9613C24.9202 20.0656 20.0721 24.9137 18.9678 31.1069C18.7631 32.2548 18.7382 33.554 18.7353 35.396C18.7316 35.5486 18.73 35.7164 18.7291 35.9161C20.6319 35.8358 22.4581 35.4599 24.163 34.8328C24.6568 29.1638 29.1638 24.6566 34.8329 24.1629C35.4601 22.4577 35.8358 20.6311 35.916 18.7281ZM0.00018692 18.7281C0.080677 20.6383 0.459244 22.4712 1.09065 24.1819C6.64078 24.7602 11.0338 29.191 11.5541 34.7583C13.2585 35.4084 15.0872 35.8064 16.9948 35.9071C16.9939 35.7088 16.9922 35.5421 16.9884 35.3901C16.9855 33.5513 16.9602 32.2536 16.7557 31.1069C15.6514 24.9137 10.8034 20.0656 4.61018 18.9613C3.39668 18.745 2.01413 18.7289 0.00018692 18.7281ZM17.1873 9.53674e-05C15.2845 0.0803684 13.4583 0.456338 11.7534 1.08342C11.2597 6.75246 6.75252 11.2595 1.08351 11.7533C0.456264 13.4586 0.0803909 15.2852 0.00018692 17.1883C2.11922 17.1882 3.55089 17.1782 4.80268 16.955C10.9958 15.8508 15.8439 11.0027 16.9482 4.80954C17.1527 3.66273 17.178 2.36501 17.1809 0.526061C17.1847 0.371844 17.1864 0.202388 17.1873 9.53674e-05ZM18.7291 9.53674e-05C18.73 0.199843 18.7316 0.367575 18.7353 0.520233C18.7382 2.36227 18.7631 3.66152 18.9678 4.80954C20.0721 11.0027 24.9202 15.8508 31.1133 16.955C32.3652 17.1783 33.7968 17.1882 35.916 17.1883C35.8358 15.2852 35.4601 13.4585 34.8329 11.7533C29.1638 11.2596 24.6567 6.75251 24.163 1.08342C22.4581 0.456314 20.6318 0.0803975 18.7291 9.53674e-05Z"
      fill="#fff"
    />
  </svg>
);

export function StardeckSignInButton({
  size = "medium",
  children = "Sign in with Stardeck",
  className = "",
  style = {},
  onSuccess,
  onError,
  disabled = false,
}: StardeckSignInButtonProps) {
  const { signIn, isLoading: authLoading } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);

  const handleSignIn = async () => {
    if (isSigningIn || authLoading || disabled) return;

    setIsSigningIn(true);
    try {
      await signIn();
      onSuccess?.();
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Sign in failed");
      onError?.(err);
    } finally {
      setIsSigningIn(false);
    }
  };

  const isDisabled = disabled || isSigningIn || authLoading;
  const showLoading = isSigningIn || authLoading;

  const baseStyles: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    color: "#ffffff",
    backgroundColor: "#CA6283",
    border: "none",
    cursor: isDisabled ? "not-allowed" : "pointer",
    opacity: isDisabled ? 0.6 : 1,
    transition: "all 0.2s ease",
    outline: "none",
    position: "relative",
    overflow: "hidden",
    ...sizeStyles[size],
  };

  const hoverStyles: CSSProperties = isDisabled
    ? {}
    : {
        backgroundColor: "#b85673",
        transform: "translateY(-1px)",
        boxShadow: "0 4px 12px rgba(202, 98, 131, 0.3)",
      };

  const focusStyles: CSSProperties = {
    boxShadow: "0 0 0 3px rgba(202, 98, 131, 0.2)",
  };

  const spinnerSize = size === "small" ? 14 : size === "medium" ? 16 : 18;
  const logoSize = size === "small" ? 16 : size === "medium" ? 20 : 24;

  return (
    <button
      className={className}
      style={{
        ...baseStyles,
        ...style,
      }}
      onClick={handleSignIn}
      disabled={isDisabled}
      onMouseEnter={(e) => {
        if (!isDisabled) {
          Object.assign(e.currentTarget.style, hoverStyles);
        }
      }}
      onMouseLeave={(e) => {
        if (!isDisabled) {
          e.currentTarget.style.backgroundColor = "#CA6283";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }
      }}
      onFocus={(e) => {
        Object.assign(e.currentTarget.style, focusStyles);
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
      aria-label="Sign in with Stardeck"
      aria-disabled={isDisabled}
      aria-busy={showLoading}
    >
      {showLoading ? (
        <>
          <LoadingSpinner size={spinnerSize} />
          <span style={{ marginLeft: sizeStyles[size].gap }}>Signing in...</span>
        </>
      ) : (
        <>
          <StardeckLogo size={logoSize} />
          <span style={{ marginLeft: sizeStyles[size].gap }}>{children}</span>
        </>
      )}
    </button>
  );
}
